<template>
  <div class="page main-page padding-2xl-vertical">
    <div
      class="container padding-l-vertical bg-bg-4 margin-2xl-bottom border-gray-2 border-radius">
      <div class="flex-wrap gap-m">
        <form-button
          v-for="tabName in tabList"
          :text="'sg.tabs.' + tabName"
          :theme="tab === tabName ? 'lead' : 'gray-5'"
          @click="tab = tabName">
        </form-button>
      </div>
    </div>

    <transition-group
      enter-active-class="animate__fadeIn animate--fast "
      leave-active-class="test">
      <card
        v-show="tab === 'margins'"
        title="sg.tabs.margins"
        class="container">
        <div class="tab-content margin-xl-top">
          <div
            v-for="margin in margins"
            class="margin-demo-block margin-l-bottom">
            <p class="desc margin-s-bottom">
              <span>{{ translate("sg.margins.width") }}</span
              >&nbsp<span>{{ translate("sg.margins." + margin.name) }}</span>
              <span>: </span>
              <strong>{{ margin.name }}</strong>
              <br />
              <span class="ltr">{{ margin.val }}</span>
            </p>
            <div
              :ref="'mb-' + margin.name"
              class="bg-gray-4"
              :style="{ 'padding-inline-end': `var(--margin-${margin.name})` }"
              style="width: 0; height: 5px"></div>
          </div>
        </div>

        <h3 class="tab-title">{{ translate("sg.radius.title") }}</h3>
        <div class="tab-content margin-xl-top">
          <div
            v-for="radius of borderRadiuses"
            class="margin-demo-block margin-l-bottom">
            <p class="desc margin-s-bottom">
              <span>{{ translate("sg.radius.radius") }}</span>
              <span> </span>
              <span>: </span>
              <strong>{{ translate("sg.radius." + radius.name) }}</strong>
              <br />
              <span class="ltr">{{ radius.val }}</span>
            </p>
            <div class="demo flex flex-gap-xl flex-middle">
              <div
                class="bg-gray-4"
                :style="{
                  'border-radius': `var(--border-radius-${radius.name}`,
                }"
                style="height: 100px; width: 100px"></div>

              <div
                class="bg-gray-4"
                :style="{
                  'border-radius': `var(--border-radius-${radius.name}`,
                }"
                style="height: 60px; width: 100px"></div>

              <div
                class="bg-gray-4"
                :style="{
                  'border-radius': `var(--border-radius-${radius.name}`,
                }"
                style="height: 40px; width: 100px"></div>
            </div>
          </div>
        </div>
      </card>

      <card v-show="tab === 'colors'" title="sg.tabs.colors" class="container">
        <block
          v-for="(colors, name) of colorPallet"
          class="margin-l-bottom"
          :colors="colors">
          <h3 class="block margin-m-bottom">
            {{ ucFirst(translate(`sg.colorGroups.${name}`)) }}
          </h3>
          <block class="flex gap-m flex-wrap">
            <block
              v-for="color of colors"
              class="margin-xl-bottom"
              :color="color">
              <span class="block padding-s text-center">{{
                ucFirst(color)
              }}</span>
              <block
                style="
                  height: 100px;
                  width: 150px;
                  flex: 100px;
                  border: 3px solid var(--c-gray);
                ">
                <block
                  :class="'bg-' + color"
                  style="height: 100%; width: 100%"></block>
              </block>
            </block>
          </block>
        </block>
      </card>

      <card
        v-show="tab === 'typography'"
        title="sg.tabs.typography"
        class="container">
        <span class="text-hero">Hero / Jumbotron text</span>
        <h1>Heading One, Lorem Ipsum.</h1>

        <h2>Heading Two, Lorem Ipsum dolor.</h2>

        <h3>Heading Three, Lorem Ipsum dolor sit amet.</h3>

        <h4>
          Heading Four. Lorem ipsum dolor sit amet, consectetur adipisicing
          elit. Amet debitis distinctio eius rerum vel!
        </h4>

        <h5>
          Heading Five. Lorem ipsum dolor sit amet, consectetur adipisicing
          elit. Amet debitis distinctio eius rerum vel!
        </h5>

        <h6>
          Heading six. Lorem ipsum dolor sit amet, consectetur adipisicing elit.
          Amet debitis distinctio eius rerum vel!
        </h6>

        <p>These are paragraphs.</p>

        <p>
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Dolorem eum
          excepturi illum.<br />
          <a href="#">Some link</a> impedit in inventore ipsa, iure labore
          laudantium minima omnis qui quis rem rerum similique ullam voluptas
          voluptates. Nam?
        </p>

        <hr class="margin-2xl-bottom" />

        <h1 class="margin-s-bottom">Example article</h1>

        <h4>Sub title for the article, it is very interesting</h4>

        <p>
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Aut culpa ex
          exercitationem inventore quo tenetur vero. A exercitationem ipsum
          iusto laborum nam placeat voluptates! Adipisci aliquid aperiam
          architecto aspernatur minus?
        </p>

        <p>
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Aut culpa ex
          exercitationem inventore quo tenetur vero. A exercitationem ipsum
          iusto laborum nam placeat voluptates! Adipisci aliquid aperiam
          architecto aspernatur minus?
        </p>

        <p>
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Aut culpa ex
          exercitationem inventore quo tenetur vero. A exercitationem ipsum
          iusto laborum nam placeat voluptates! Adipisci aliquid aperiam
          architecto aspernatur minus?
        </p>

        <div class="spacer margin-max-bottom"></div>
      </card>

      <card v-show="tab === 'icons'" title="sg.tabs.icons" class="container">
        <div class="flex gap-m flex-wrap flex-stretch margin-bottom">
          <div
            v-for="icon of allIcons"
            :key="icon.uniqueKey"
            class="block flex-center text-center flex-middle bg-light flex-wrap border-thin-gray-1 border-radius bg-bg-4 width-150"
            :icon="icon.icon"
            style="min-height: 90px; min-width: 130px">
            <block
              class="block width-expand text-center flex-middle flex-center flex-middle padding-s">
              <icon :icon="icon.icon" ratio="2"></icon>
            </block>
            <block
              style="margin-top: auto"
              class="block width-expand margin-bottom text-center flex-middle flex-center padding-s-horizontal">
              <span>{{ icon.icon }}</span>
              <span  v-if="!icon.isFromCore"><br>({{ 'extended' }})</span>
              <span v-if="defaultIconSet !== icon.set"><br>({{ icon.set }})</span>
            </block>
          </div>
        </div>
      </card>

      <div
        v-show="tab === 'spinners'"
        class="container margin-2xl-bottom animate__fadeIn">
        <card title="sg.tabs.spinnersOverlay">
          <bar-loader :active="true"></bar-loader>
          <spinner
            :show="true"
            text="core.test"
            :overlay="'absolute'"
            :overlay-opacity="0.6"></spinner>
        </card>
        <card title="sg.tabs.spinners">
          <div class="flex flex-start flex-wrap flex-stretch flex-middle gap-m">
            <div
              class="block flex-column flex-top flex-middle bg-gray-2 padding radius-s">
              <block class="margin-m-bottom"
                ><span>Default with text</span></block
              >
              <spinner text="core.test"></spinner>
            </div>
            <div
              class="block flex-column flex-top flex-center flex-middle bg-gray-2 padding radius-s">
              <block class="margin-m-bottom"><span>circle, size 3</span></block>
              <spinner type="circle" size="3"></spinner>
            </div>
            <div
              class="block flex-column flex-top flex-center flex-middle bg-gray-2 padding radius-s">
              <block class="margin-m-bottom"
                ><span>size 2, color alt</span></block
              >
              <spinner color="alt" size="2"></spinner>
            </div>
            <div
              class="block flex-column flex-top flex-center flex-middle bg-gray-2 padding radius-s">
              <block class="margin-m-bottom"><span>w/ progress</span></block>
              <spinner :progress="75"></spinner>
            </div>
          </div>
        </card>
      </div>

      <card
        v-show="tab === 'buttons'"
        title="sg.tabs.buttons"
        class="container">
        <div class="flex flex-wrap gap-s margin-l-bottom">
          <form-button size="large">Large</form-button>
          <form-button>Regular / medium</form-button>
          <form-button size="small">small</form-button>
        </div>
        <div class="flex flex-wrap gap-s margin-l-bottom">
          <form-button theme="alt">Color alt</form-button>
          <form-button theme="lead">Color lead</form-button>
          <form-button theme="lead-2">lead2</form-button>
          <form-button theme="comp-lead">Comp lead</form-button>
          <form-button theme="danger">DANGER!</form-button>
          <form-button theme="warning">warning</form-button>
          <form-button theme="success">Success!</form-button>
        </div>
        <div class="flex flex-wrap gap-s margin-l-bottom">
          <form-button icon="check" size="large" theme="alt"
            >Large icon</form-button
          >
          <form-button icon="check" theme="alt">Icon Start</form-button>
          <form-button icon-end="email" theme="alt">Icon End</form-button>
          <form-button :disabled="true">Disabled</form-button>
          <form-button :disabled="true" theme="alt">Disabled</form-button>
        </div>
        <div class="flex flex-wrap gap-s margin-l-bottom">
          <form-button icon="check" theme="alt" :loading="true"
            >Icon loading</form-button
          >
          <form-button class=""></form-button>
          <form-button text="core.test"></form-button>
        </div>
        <div class="flex flex-wrap gap-s margin-l-bottom">
          <form-button :full-width="true">Full Width</form-button>
        </div>
      </card>

      <card v-show="tab === 'badges'" title="sg.tabs.badges" class="container">
        <div class="flex-start flex-wrap flex-middle gap-m">
          <badge size="l" theme="lead">lead - large</badge>
          <badge size="m" theme="lead">
            <icon icon="check" />
          </badge>
          <badge size="s" theme="danger">No!</badge>
          <clean-wrapper
            v-for="(colorGroups, index) of colorPallet"
            :key="index">
            <badge v-for="color in colorGroups" :key="color" :theme="color"
              >{{ color }}
            </badge>
          </clean-wrapper>
        </div>
      </card>

      <card v-show="tab === 'forms'" title="sg.tabs.forms" class="container">
        <base-form :config="exampleFormConfig1"></base-form>
      </card>

      <block v-show="tab === 'cards'" class="cards">
        <div v-show="tab === 'cards'" class="margin-xl-bottom animate__fadeIn">
          <div class="card container">
            <div class="card__header">
              <span class="card__title">Card title</span>
              <span class="card__sub-title"
                >Card sub title. This is 8 words long.</span
              >
            </div>
            <div class="card__body">
              <p>
                Lorem ipsum dolor sit amet,
                <a href="#">consectetur adipisicing elit</a>. Ab corporis
                delectus dolor doloribus eveniet fugiat in obcaecati praesentium
                quam quia quos repudiandae rerum temporibus veritatis,
                voluptatibus. Deleniti eos maxime sapiente.
              </p>
            </div>
          </div>
          <card
            class="margin-xl-top container"
            title="core.createdSuccess"
            icon="user"
            sub-title="core.selectPlaceholder"
            badge-text="core.ok"
            badge-icon="arrow-download"
            badge-theme="light"
            :badge-action="alertExample"
            theme="lead">
            <template #sub-title="{ card }"> sub title free</template>
            <template #default="{ card }">
              <p>
                Lorem ipsum dolor sit amet, consectetur adipisicing elit.
                Aspernatur cumque doloremque eveniet reiciendis veniam!
                Accusamus delectus eveniet ipsum iste laboriosam, minus nesciunt
                quaerat quo saepe veritatis. Ducimus ea nam quidem?
              </p>
            </template>

            <template #footer="{ card }">
              <block class="flex flex-end flex-middle gap-m">
                <anchor href="#" class="">Forgot?</anchor>
                <div class="flex gap-m margin-remove">
                  <form-button theme="inverse">Cancel</form-button>
                  <form-button theme="light" icon-end="chevron-inline-end"
                    >OK
                  </form-button>
                </div>
              </block>
            </template>
          </card>
        </div>

        <div
          v-show="tab === 'cards'"
          class="container margin-xl-bottom flex-between gap-xl animate__fadeIn">
          <div class="card">
            <div class="card__header">
              <span class="card__title">
                <icon icon="check"></icon>
                <span>Card title Card title Card </span>
                <badge size="l">Large Badge</badge>
              </span>
              <span class="card__sub-title">
                <span>Card sub title. This is 8 words long. </span>
              </span>
            </div>
            <div class="card__body">
              <p>
                Lorem ipsum dolor sit amet,
                <a href="#">consectetur adipisicing elit</a>. Ab corporis
                delectus dolor doloribus eveniet fugiat in obcaecati praesentium
                quam quia quos repudiandae rerum temporibus veritatis,
                voluptatibus. Deleniti eos maxime sapiente.
              </p>
            </div>

            <div class="card__footer">
              <span class="">Card footer</span>
            </div>
          </div>
          <div class="card card--tight">
            <div class="card__header">
              <span class="card__title"
                >Card title - tight <badge theme="inverse">Badger</badge></span
              >
              <span class="card__sub-title"
                >Card sub title. This is 8 words long.</span
              >
            </div>
            <div class="card__body">
              <p>
                Lorem ipsum dolor sit amet,
                <a href="#">consectetur adipisicing elit</a>. Ab corporis
                delectus dolor doloribus eveniet fugiat in obcaecati praesentium
                quam quia quos repudiandae rerum temporibus veritatis,
                voluptatibus. Deleniti eos maxime sapiente.
              </p>
            </div>
          </div>
        </div>

        <div
          v-show="tab === 'cards'"
          class="container margin-xl-bottom flex-between gap-xl animate__fadeIn">
          <div class="card card--inverse">
            <div class="card__header">
              <span class="card__title">Card title - inverse</span>
              <span class="card__sub-title">Card sub title. Something...</span>
            </div>
            <div class="card__body">
              <p>
                Lorem ipsum dolor sit amet,
                <a href="#">consectetur adipisicing elit</a>. Ab corporis
                delectus dolor doloribus eveniet fugiat in obcaecati praesentium
                quam quia quos repudiandae rerum temporibus veritatis,
                voluptatibus. Deleniti eos maxime sapiente.
              </p>
            </div>
          </div>
          <div class="card card--lead">
            <div class="card__header">
              <span class="card__title">Card title - lead</span>
              <span class="card__sub-title">Card sub title. Lorem.</span>
            </div>
            <div class="card__body">
              <p>
                Lorem ipsum dolor sit amet,
                <a href="#">consectetur adipisicing elit</a>. Ab corporis
                delectus dolor doloribus eveniet fugiat in obcaecati praesentium
                quam quia quos repudiandae rerum temporibus veritatis,
                voluptatibus. Deleniti eos maxime sapiente.
              </p>
            </div>
          </div>
          <div class="card card--alt">
            <div class="card__header">
              <span class="card__title">Card title - lead</span>
              <span class="card__sub-title">Card sub title. </span>
            </div>
            <div class="card__body">
              <p>
                Lorem ipsum dolor sit amet,
                <a href="#">consectetur adipisicing elit</a>. Ab corporis
                delectus dolor doloribus eveniet fugiat in obcaecati praesentium
                quam quia quos repudiandae rerum temporibus veritatis,
                voluptatibus. Deleniti eos maxime sapiente.
              </p>
            </div>
          </div>
        </div>
      </block>

      <card
        v-show="tab === 'tooltips'"
        title="sg.tabs.tooltips"
        class="container">
        <form-button
          v-tooltip="'core.testString'"
          class="margin-m-bottom"
          text="Some text"
          :auto-translate="false"
          icon="check"></form-button>

        <form-button
          v-tooltip.bottom.test="{
            content: 'Some other text',
            autoTranslate: false,
            theme: 'gray-5',
          }"
          text="Other  text"
          :auto-translate="false"
          icon="check"></form-button>

        <div class="spacer" style="height: 400px"></div>
      </card>

      <card v-show="tab === 'panels'" title="sg.tabs.panels" class="container">
        <block>
          <div class="panel margin-l-bottom">
            <h3>Panel</h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipisicing elit.
              Accusantium ad amet aut autem dolore dolores, eos fugit ipsa ipsum
              itaque molestiae mollitia nemo non porro, praesentium quidem sed
              suscipit, tempore?
            </p>
          </div>

          <div class="flex gap-l">
            <div class="panel panel--lead">
              <h3>Panel</h3>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipisicing elit.
                Accusantium ad amet aut autem dolore dolores, eos fugit ipsa
                ipsum itaque molestiae mollitia nemo non porro, praesentium
                quidem sed suscipit, tempore?
              </p>
            </div>

            <div class="panel panel--alt">
              <h3>Panel</h3>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipisicing elit.
                Accusantium ad amet aut autem dolore dolores, eos fugit ipsa
                ipsum itaque molestiae mollitia nemo non porro, praesentium
                quidem sed suscipit, tempore?
              </p>
            </div>

            <div class="panel panel--inverse">
              <h3>Panel</h3>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipisicing elit.
                Accusantium ad amet aut autem dolore dolores, eos fugit ipsa
                ipsum itaque molestiae mollitia nemo non porro, praesentium
                quidem sed suscipit, tempore?
              </p>
            </div>
          </div>

          <div class="panel panel--tight margin-l-top">
            <h3 class="panel__title">Panel</h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipisicing elit.
              Accusantium ad amet aut autem dolore dolores, eos fugit ipsa ipsum
              itaque molestiae mollitia nemo non porro, praesentium quidem sed
              suscipit, tempore?
            </p>
          </div>
        </block>
      </card>

      <card v-show="tab === 'modals'" title="sg.tabs.modal" class="container">
        <div>
          <form-button @click="$refs.testModal.showModal()"
            >Show Modal</form-button
          >
        </div>
        <modal
          ref="testModal"
          :title="'core.testString'"
          :close-button="true"
          :show-footer="true"
          :show="tempModalShow">
          <template #default>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ad
              commodi cupiditate dicta dignissimos doloremque dolores ex
              explicabo fuga iusto magni perspiciatis quas repellendus totam,
              vero, vitae! Explicabo modi, sint. Illo?
            </p>
          </template>
        </modal>
      </card>

      <card v-show="tab === 'alerts'" title="sg.tabs.alerts" class="container">
        <block
          v-for="style in alertStyles"
          :key="style"
          class="margin-xl-bottom">
          <block>
            <alert type="primary" :theme="style" close-type="default">
              <template #title="{ alert }">
                <icon v-if="style === 'default'" icon="eye" />
                <icon v-if="style === 'success'" icon="check" />
                <icon v-if="style === 'danger'" icon="x" />
                <icon v-if="style === 'warning'" icon="warning-triangle" />
                <span>Heads up!</span>
              </template>
              <template #default="{ alert }">
                <block class="flex-between flex-bottom gap-l">
                  <p>
                    This is an alert of style - <b>{{ style }}</b
                    >. <br />Lorem ipsum dolor sit amet, consectetur adipisicing
                    elit.
                    <a href="#">Ipsam, nihil unde. Accusamus</a> accusantium
                    architecto aut!
                  </p>
                  <form-button
                    v-if="['default', 'warning', 'gray'].includes(style)"
                    :theme="'bg-transparent'"
                    icon="check"
                    @click="alert.close()"></form-button>
                </block>
              </template>
            </alert>
          </block>
        </block>
      </card>

      <card
        v-show="tab === 'breadcrumbs'"
        title="sg.tabs.breadcrumbs"
        class="container">
        <breadcrumbs class="margin-l-bottom"></breadcrumbs>
        <breadcrumbs size="l"></breadcrumbs>
        <span class="block margin-l-top font-m"
          >* try
          <a
            href="#"
            @click.prevent="$router.push({ name: 'some-nested-page' })"
            >this page</a
          >
          for a deeper breadcrumb</span
        >
      </card>

      <card
        v-if="tab === 'notifications'"
        title="sg.tabs.notifications"
        class="container">
        <notification
          theme="default"
          title="Notification title!"
          content="Default notification - no auto-close"
          icon="user"
          :location="false"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="danger"
          title="Oh no!"
          content="This has completely and utterly failed! I am devistated!"
          icon="warning-triangle"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="warning"
          title="Something isn't right..."
          content="This makes me nervous..."
          icon="warning-triangle"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="success"
          title="Awesome!"
          content="That went just according to plan!"
          icon="check"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="alt"
          title="Alt theme notification!"
          content="Something has happened. No icon though..."
          :auto-translate="false">
        </notification>
        <notification
          theme="comp-lead"
          title="Notification title!"
          content="Comp-lead notification color scheme"
          icon="check"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="inverse"
          title="Notification title!"
          content="Inverse notification."
          icon="check"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="default"
          title="Long text!"
          content="Lorem ipsum dolor sit amet, consectetur adipisicing elit. A ab aperiam, architecto asperiores consequuntur dolor dolore ducimus, exercitationem facilis impedit iusto modi officiis quaerat, qui sapiente sint suscipit voluptate voluptatibus?"
          icon="check"
          :auto-close="false"
          :auto-translate="false">
        </notification>
        <notification
          theme="default"
          title="Auto close is on!"
          content="This notification will close it self after the preset time (700ms). Hover me to reset the countdown"
          icon="check"
          :auto-close="true"
          :auto-translate="false">
        </notification>
        <notification
          :auto-close="false"
          title="core.defaultNotificationTitle"
          content="core.sampleVeryShortText">
        </notification>
        <notification
          theme="default"
          title="Custom slots!"
          icon="chevron-inline-end"
          :auto-close="false"
          :auto-translate="false">
          <span>
            Notification with
            <a href="#">custom slot html. This also uses slot props</a></span
          >
          <template #title="{ close }">
            <span
              class="notification__title"
              style="cursor: pointer"
              @click="close()"
              >Custom close title</span
            >
          </template>
        </notification>
        <notification
          theme="comp-alt"
          location="snackbar"
          title="Notification title!"
          content="Comp-alt notification color scheme"
          icon="arrow-download"
          animation="flipInX"
          close-animation="flipOutX"
          :auto-close="false"
          :auto-translate="false">
        </notification>
      </card>

      <card
        v-show="tab === 'pagination'"
        title="sg.tabs.pagination"
        class="container">
        <pagination
          :total-count="1000"
          :current-page="5"
          :page-size="100"
          :force-show="true"></pagination>
      </card>

      <card v-show="tab === 'tables'" title="sg.tabs.tables" class="container">
        <table class="table table--highlight table--large">
          <thead>
            <tr>
              <th class="table--shrink table--center">ID</th>
              <th class="table--expand">Expand</th>
              <th>Table Heading</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="table--shrink table--center">1</td>
              <td>Table Data</td>
              <td>Table Data</td>
            </tr>
            <tr>
              <td class="table--shrink table--center">2</td>
              <td>Table Data</td>
              <td>Table Data</td>
            </tr>
            <tr>
              <td class="table--shrink table--center">3</td>
              <td>Table Data</td>
              <td class="table--small table--truncate">
                Table Data Table Data Table Data
              </td>
            </tr>
          </tbody>
        </table>

        <pagination
          class="margin-xl-top"
          :total-count="1000"
          :current-page="5"
          :page-size="100"
          :force-show="true"></pagination>
      </card>
    </transition-group>
  </div>

  <div v-show="tab === 'containers'" class=" ">
    <div class="container container-l">
      <h2 class="margin-remove-bottom">
        {{ translateTitleCase("sg.containers.title") }}
      </h2>
      <p>{{ translate("sg.containers.explain") }}</p>
    </div>

    <div class="spacer margin-max-bottom"></div>
    <div
      v-for="container in containers"
      class="container bg-gray flex-center flex-middle border-thin-gray-4 box-shadow margin-2xl-bottom"
      style="height: 140px"
      :class="['container-' + container.name]">
      <div
        class="content text-center bg-gray-1 flex-middle flex-center"
        style="height: 100%; flex: 0 0 100%; width: 100%">
        <block>
          <strong class="block text-center">{{
            translate("sg.containers.container") +
            " - " +
            translate("sg.containers." + container.name)
          }}</strong>
          <span class="block text-center">{{ container.val }}</span>
        </block>
      </div>
    </div>
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { useStore } from "vuex";

let allIconsGlobal = {};
let appName = process.env.VUE_APP_APPLICATION_NAME;
let defaultIconSet = config.style.defaultIconSet;

let getIconObject = (key, isFromCore = false) => {
  let fullPath = key;
  let split = key.split("/");
  let set = split[split.length - 2];
  let iconName = split[split.length - 1].split(".")[0];
  let uniqueKey;

  uniqueKey = set + "-" + iconName;

  return {
    uniqueKey: uniqueKey,
    icon: iconName,
    set: set,
    path: fullPath,
    isFromCore,
  };
};

let addIconObject = (key,isFromCore) => {
  let obj = getIconObject(key,isFromCore);
  allIconsGlobal[obj.uniqueKey] = obj;
};

let context = require.context("@/", true, /\/static\/icons\/.*\/.*\.*.svg/);
context.keys().forEach((key) => {
  addIconObject(key,true);
});

context = require.context(
  "@/client/applications/",
  true,
  /\/static\/icons\/.*\/.*\.*.svg/
);
context.keys().forEach((key) => {
  if (key.startsWith(`./${appName}/`)) {
    addIconObject(key, false);
  }
});

export default {
  components: {},
  layout: "default",
  middleware: {},
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(
      props,
      useStore()
    );
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  meta: "sg.pageTitle",
  data() {
    return {
      tempModalShow: false,
      tempFormModel: {
        clientName: 3,
      },
      colorPallet: {
        lead: ["lead-1", "lead-2", "lead", "lead-4", "lead-5"],
        alt: ["alt-1", "alt-2", "alt", "alt-4", "alt-5"],
        complimentaryLead: [
          "comp-lead-1",
          "comp-lead-2",
          "comp-lead",
          "comp-lead-4",
          "comp-lead-5",
        ],
        complimentaryAlt: [
          "comp-alt-1",
          "comp-alt-2",
          "comp-alt",
          "comp-alt-4",
          "comp-alt-5",
        ],
        "gray-scale": ["gray-1", "gray-2", "gray", "gray-4", "gray-5"],
        background: ["bg-1", "bg-2", "bg", "bg-4", "bg-5"],
        other: ["text", "inverse"],
        ui: [
          "success",
          "success-inverse",
          "warning",
          "warning-inverse",
          "danger",
          "danger-inverse",
        ],
      },
      exampleIcons: [
        "check",
        "alarm",
        "archive",
        "cube",
        "arrow-left",
        "arrow-right",
        "bag",
        "bell",
        "battery-half",
        "bookmark",
        "user",
        "chevrons-left",
        "clock",
        "clone",
        "cloud",
      ],
      alertStyles: [
        "default",
        "lead",
        "alt",
        "success",
        "warning",
        "danger",
        "gray",
      ],
      exampleFormConfig1: {
        fields: {
          test: {
            name: "test",
            icon: "lock",
            iconEnd: "eye",
            label: "core.form.password",
            placeholder: "core.form.placeholders.password",
            type: "password",
            validation: {
              required: {},
            },
            group: "group1",
          },
          color: {
            name: "color",
            icon: "color-palette",
            type: "color",
            label: "core.form.email",
            placeholder: "core.form.placeholders.email",
            validation: {
              required: {},
            },
            group: "group1",
          },
          testTextArea: {
            name: "testTextArea",
            icon: "color-palette",
            type: "textArea",
            label: "core.form.email",
            characterCounter: {
              max: 256,
            },
            placeholder: "core.form.placeholders.email",
            validation: {
              required: {},
            },
            group: "group1",
          },
          testSelect: {
            name: "testSelect",
            icon: "email",
            label: "core.form.email",
            placeholder: "core.form.placeholders.email",
            nullOption: {
              value: null,
              label: "core.selectPlaceholder",
            },

            // showChevron: 'false',
            showFilterField: true,
            type: "select2",
            list: [
              {
                value: "1",
                label: "core.form.name",
              },
              {
                value: "2",
                label: "core.form.password",
              },
              {
                value: "3",
                label: "core.form.fullName",
              },
              {
                value: "4",
                label: "core.form.fullName",
              },
              {
                value: "5",
                label: "core.form.fullName",
              },
              {
                value: "6",
                label: "core.form.fullName",
              },
              {
                value: "7",
                label: "core.form.fullName",
              },
              {
                value: "8",
                label: "core.form.fullName",
              },
              {
                value: "9",
                label: "core.form.fullName",
              },
              {
                value: "10",
                label: "core.form.fullName",
              },
            ],
            validation: {
              required: {},
            },
            group: "group1",
          },
          testCheckbox: {
            name: "testCheckbox",
            type: "checkbox",
            //theme: "warning",
            label: "core.form.password",
            placeholder: "core.form.placeholders.password",
            validation: {
              //  required: {},
            },
            group: "group2",
          },
          testRadio: {
            name: "testRadio",
            label: "core.form.email",
            placeholder: "core.form.placeholders.email",
            nullOption: {
              value: null,
              label: "core.selectPlaceholder",
            },

            // showChevron: 'false',
            type: "radio",
            list: [
              {
                value: "1",
                label: "core.form.name",
              },
              {
                value: "2",
                label: "core.form.password",
              },
              {
                value: "3",
                label: "core.form.fullName",
              },
            ],
            validation: {
              required: {},
            },
            group: "group2",
          },
          testRadio2: {
            name: "testRadio2",
            label: "core.form.password",
            placeholder: "core.form.placeholders.email",
            radioStacked: true,
            nullOption: {
              value: null,
              label: "core.selectPlaceholder",
            },

            // showChevron: 'false',
            type: "radio",
            list: [
              {
                value: "1",
                label: "core.form.name",
              },
              {
                value: "2",
                label: "core.form.password",
              },
              {
                value: "3",
                label: "core.form.fullName",
              },
            ],
            validation: {
              required: {},
            },
            group: "group2",
          },
          /*
           testSelect2: {
           name: 'testSelect2',
           icon: "email",
           label:"core.form.email",
           placeholder:"core.form.placeholders.email",
           nullOption : {
           value: null,
           label: 'core.selectPlaceholder',
           },

           // showChevron: 'false',
           type: 'select2',
           list: [
           {
           value: '1',
           label: 'core.form.name'
           },
           {
           value: '2',
           label: 'core.form.password'
           },
           {
           value: '3',
           label: 'core.form.fullName'
           },
           ],
           validation: {
           required: {},
           }
           },*/
          testRep: {
            name: "testRep",
            type: "repeatable",
            label: "core.test",
            fields: {
              repTest: {
                name: "repTest",
                type: "text",
                label: "entity.bogle.fields.test.repTestLabel",
                validation: { required: {}, minLength: { params: { min: 6 } } },
              },
              repBogle: {
                name: "repBogle",
                type: "select",
                list: [
                  { value: "0", label: "Option 0" },
                  {
                    value: "1",
                    label: "Option 1",
                  },
                  { value: "2", label: "Option 2" },
                ],
                label: "entity.bogle.fields.test.repBogleLabel",
              },
            },
            group: "group1",
          },
          test3: {
            name: "test3",
            label: "core.test",
            placeholder: "core.testString",
            validation: {
              required: {},
            },
            group: "group2",
          },
        },
        groups: {
          group1: {
            component: "FormFieldSet",
            props: {
              title: "core.testString",
              icon: "user",
            },
          },
          group2: {
            component: "FormFieldSet",
            props: {
              title: "core.test",
              icon: "arrow-download",
            },
          },
        },

        formHandling: {
          preflight: [
            {
              type: "validation",
              options: {},
            },
            {
              type: "confirmation",
              options: {},
            },
          ],
        },

        buttons: {
          test: {
            label: "core.form.submit",
          },
        },
        //errorsAbsolute: false
      },
      margins: [
        {
          name: "xs",
          size: "2",
          val: "",
        },
        {
          name: "s",
          size: "5",
          val: "",
        },
        {
          name: "m",
          size: "10",
          val: "",
        },
        {
          name: "l",
          size: "20",
          val: "",
        },
        {
          name: "xl",
          size: "30",
          val: "",
        },
        {
          name: "2xl",
          size: "45",
          val: "",
        },
        {
          name: "max",
          size: "60",
          val: "",
        },
      ],
      borderRadiuses: [
        {
          name: "s",
          size: 6,
          val: "",
        },
        {
          name: "m",
          size: 20,
          val: "",
        },
        {
          name: "l",
          size: 35,
          val: "",
        },
        {
          name: "round",
          size: 35,
          val: "",
        },
      ],
      containers: [
        {
          name: "xs",
          val: "",
        },
        {
          name: "s",
          val: "",
        },
        {
          name: "m",
          val: "",
        },
        {
          name: "l",
          val: "",
        },
      ],
      asyncData: {
        /*
         summaryData: {
         target: 'project/summary',
         data: {
         'from': computed(() => {
         let d = new Date();
         d.setFullYear( d.getFullYear() - 1);
         d.setDate(1);
         return d.toISOString().split('T')[0]
         })
         }
         },
         statuses: 'project/status'
         */
      },
      tabList: [
        "icons",
        "margins",
        "colors",
        "typography",
        "containers",

        "spinners",
        "buttons",
        "badges",
        "forms",
        "cards",
        "tooltips",
        "panels",
        "modals",
        "alerts",
        "breadcrumbs",
        "notifications",
        "pagination",
        "tables",
      ],
      tab: "icons",
    };
  },
  computed: {
    allIcons() {
      return allIconsGlobal;
    },
    defaultIconSet() {
      return defaultIconSet;
    },
  },

  watch: {},
  mounted() {
    utilities.wait(300).then(() => {
      this.populateCssVariables();
    });
  },
  methods: {
    ucFirst(string) {
      return utilities.ucFirst(string);
    },
    logEvent(a) {
      console.log("logged event!", a);
    },
    alertExample() {
      alert("alert example");
    },
    getCssVar(varName) {
      return getComputedStyle(document.querySelector("#app")).getPropertyValue(
        "--" + varName
      );
    },
    populateCssVariables() {
      this.margins = this.margins.map((margin) => {
        return {
          ...margin,
          val: this.getCssVar("margin-" + margin.name),
        };
      });

      this.borderRadiuses = this.borderRadiuses.map((radius) => {
        return {
          ...radius,
          val: this.getCssVar("border-radius-" + radius.name),
        };
      });

      this.containers = this.containers.map((container) => {
        return {
          ...container,
          val: this.getCssVar("container-" + container.name),
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
